/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import { useMemo } from 'react';
import { produce } from 'immer';

import { mergeSearchResultWithState } from '@/page/catalog-vehicles/store/catalog-filter.schemas';
import { createInitialFilterState } from '@/page/catalog-vehicles/store/catalog-filter.store';
import { type SelectedFilters } from './search-history-section.component';
import { reduceFiltersTo } from './filter-encoding';
import {
  encodeBodyType,
  encodeDrivetrain,
  encodeExteriorColor,
  encodeFeatures,
  encodeFuelType,
  encodeInteriorColor,
  encodeInventoryType,
  encodeListingType,
  encodeMakeModel,
  encodeMileage,
  encodePrice,
  encodeSeatingCapacity,
  encodeTransmission,
  encodeYear,
} from './filter-encoding/encoders';

export default function useParseSelectedFilters(
  query: string,
  selectedFilters: SelectedFilters
): string {
  const filters = useMemo(
    () =>
      produce(createInitialFilterState(), (draft) => {
        mergeSearchResultWithState(draft, selectedFilters);
      }),
    [selectedFilters]
  );

  return useMemo(
    () =>
      // WARN: The filter encoders are in the order they need to appear. Do not change their order pls.
      reduceFiltersTo(query.length > 0 ? [query] : [])
        .pipe(
          encodeYear(filters.year),
          encodeMakeModel(filters.makeModels, query),
          encodeBodyType(filters.bodyType),
          encodeListingType(filters.listingType),
          encodePrice(filters.price),
          encodeMileage(filters.mileage),
          encodeInventoryType(filters.inventoryType),
          encodeExteriorColor(filters.exteriorColor),
          encodeInteriorColor(filters.interiorColor),
          encodeFuelType(filters.fuelType),
          encodeTransmission(filters.transmission),
          encodeDrivetrain(filters.drivetrain),
          encodeSeatingCapacity(filters.seatingCapacity),
          encodeFeatures(filters.features)
        )
        .join(', '),
    [filters, query]
  );
}
